import {MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {Injectable, OnDestroy} from '@angular/core';
import {Project} from '@docma-mm-core/project/project';
import {User} from '@docma-mm-core/user/user';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {ConnectivityService} from '@mydocma/core';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {MmProjectPermissionsService} from '../../../mm-project/mm-project-permissions-service';
import {IPoolEditDialogData, PoolEditComponent} from '../pool-edit/pool-edit.component';
import {PoolFormService, PoolVisibilityEnum, PoolVisibilityTextEnum} from './pool-form.service';
import {PromptDialogService, MydocmaPromptButtonTypeEnum} from '@mydocma/component-library/src/lib/prompt-dialog';
import {PoolEditDataService} from './pool-edit-data.service';
import {BehaviorSubject, firstValueFrom, Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {MmViewService} from '../../../mm-view.service';
import {Pool} from '@docma-mm-core/pool/pool';
import {IPoolDefect, IUpdatePoolPayload} from './pool-api.service';
import {Fault} from '@docma-mm-core/fault/fault';
import {IPoolListToggleDialogData, PoolListToggleComponent} from '../pool-list-toggle/pool-list-toggle.component';
import { pageBySize } from '@docma-mm-core/utils/utils';
import {MmProjectDownloadService} from '../../../mm-project/mm-download.service';
import {RecentlyUsedPoolsService} from './recently-used-pools.service';
import {MmFilterService} from '../../mm-filter.service';
import { OfflineLocalModeMessageService } from '../../../mm-offline/services/offline-local-mode-message.service';
import {PoolFaultTrackerService} from './pool-fault-tracker.service';
import { Logger } from '@mydocma/common/src/lib/logger';
import { FaultPersistenceService } from '@docma-mm-core/fault/fault-persistence.service';
import { PlatformModeStateService } from '../../../mm-offline/services/platform-mode-state.service';


@Injectable()
export class PoolEditViewService implements OnDestroy {
  private dialogRef: MatDialogRef<any>;

  isOnline = false;


  private poolSavingSubject = new BehaviorSubject<boolean>(false);
  public poolSaving$ = this.poolSavingSubject.asObservable();

  private newPoolCreatedSubject = new BehaviorSubject<any>(null);
  public newPoolCreated$ = this.newPoolCreatedSubject.asObservable();

  private poolUpdatedSubject = new BehaviorSubject<any>(null);
  public poolUpdated$ = this.poolUpdatedSubject.asObservable();

  private poolDeletedSubject = new BehaviorSubject<any>(null);
  public poolDeleted$ = this.poolDeletedSubject.asObservable();

  private poolCountChangedSubject = new BehaviorSubject<{poolId: number, countChange?: number, newCount?: number}[]>(null);
  public poolCountChanged$ = this.poolCountChangedSubject.asObservable();

  private poolMembershipChangedSubject = new BehaviorSubject<{faultIds: number[], poolIds: number[], operation: 'ADD' | 'REMOVE'}>(null);
  public poolMembershipChanged$ = this.poolMembershipChangedSubject.asObservable();

  private subcriptions: Subscription[] = [];
  constructor(public dialog: MatDialog,
              private readonly connectivityService: ConnectivityService,
              private readonly notificationService: NotificationService,
              public readonly permissionService: MmProjectPermissionsService,
              public readonly poolFormService: PoolFormService,
              private readonly dialogService: PromptDialogService,
              private readonly poolEditDataService: PoolEditDataService,
              private readonly translateService: TranslateService,
              private readonly mmViewService: MmViewService,
              private readonly downloadService: MmProjectDownloadService,
              private readonly recentlyUsedPoolsService: RecentlyUsedPoolsService,
              private readonly filterService: MmFilterService,
              private readonly offlineLocalModeMessageService: OfflineLocalModeMessageService,
              private readonly poolFaultTracker: PoolFaultTrackerService,
              private readonly logger: Logger,
              private readonly faultPersistensService: FaultPersistenceService,
              private readonly platformModeStateService: PlatformModeStateService
  ) {
    this.subcriptions.push(
      this.poolEditDataService.newPoolCreated$.subscribe(params => {
        this.newPoolCreatedSubject.next(params);
        if (params?.Pool) {
          this.recentlyUsedPoolsService.addRecentlyUsedPool(params.Pool.Id);
        }
      })
    );

    this.subcriptions.push(
      this.poolEditDataService.poolUpdated$.subscribe(pool => {
        this.poolUpdatedSubject.next(pool);
      })
    );

    this.subcriptions.push(
      this.poolEditDataService.poolDeleted$.subscribe(params => {
        this.poolDeletedSubject.next(params);
      })
    );

    this.connectivityService.isOffline
      .subscribe(isOffline => {
        this.isOnline = !isOffline;
      });


  }

  showEditPoolDialog(scope: EntityScopeEnum, project: Project, user: User, poolToEdit?: Pool) {
    if (!this.isAppOnline(true)) {
      return;
    }

    const dialogData: IPoolEditDialogData = {
      scope,
      project,
      user,
      poolToEdit,
      currentUser: user
    };

    const ua = (typeof navigator !== 'undefined' && navigator?.userAgent) ? navigator.userAgent : '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || ((navigator as any)?.platform === 'MacIntel' && (navigator as any)?.maxTouchPoints > 1);

    const poolEditDialogClass = `mydocma-mm-pool-edit-dialog${isIOS ? '-ios' : ''}`;
    const panelClass = ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-layout-edit-dialog', poolEditDialogClass];

    this.dialogRef = this.dialog.open(PoolEditComponent, {
      data: dialogData,
      panelClass,
      autoFocus: false,
      disableClose: true,
    });

    this.dialogRef.afterClosed().subscribe(async (result) => {
      return result;
    });
  }

  async addPool(newPoolPayload: any, project: Project) {
    this.poolSavingSubject.next(true);
    try {
      await this.poolEditDataService.addPool(newPoolPayload, project);
      this.poolSavingSubject.next(false);
      this.notificationService.success(this.translateService.instant('MM.pool.leftPane.form.successAddingPool'));
      this.dialogRef.close();
    } catch (error) {
      this.logger.error(error, '', 'PoolEditViewService');
      this.poolSavingSubject.next(false);
      this.notificationService.error(this.translateService.instant('MM.pool.leftPane.form.errorAddingPool'));
    }
  }

  async updatePoolWithFormData(updatePoolPayload: IUpdatePoolPayload, project: Project, poolToUpdate: Pool) {
    this.poolSavingSubject.next(true);
    try {
      poolToUpdate.Name = updatePoolPayload.Name;
      poolToUpdate.Comment = updatePoolPayload.Comment;
      poolToUpdate.IsPublic = updatePoolPayload.IsPublic;
      poolToUpdate.VisibleFor = updatePoolPayload.IsPublic ? {
        SC: (updatePoolPayload.VisibleFor & 4) === 4,
        CO: (updatePoolPayload.VisibleFor & 2) === 2,
        CT: (updatePoolPayload.VisibleFor & 1) === 1
      } : {
        SC: true,
        CO: true,
        CT: true
      };

      const savedPool = await this.poolEditDataService.updatePool(updatePoolPayload, project, poolToUpdate);
      await this.recentlyUsedPoolsService.addRecentlyUsedPool(savedPool.Pool.Id);

      this.poolUpdatedSubject.next(savedPool);

      this.poolSavingSubject.next(false);
      this.notificationService.success(this.translateService.instant('MM.pool.leftPane.form.successUpdatingPool'));
      this.dialogRef.close();
    } catch (error) {
      this.logger.error(error, '', 'PoolEditViewService');
      this.poolSavingSubject.next(false);
      this.notificationService.error(this.translateService.instant('MM.pool.leftPane.form.errorUpdatingPool'));
    }
  }

  async deletePool(pool: Pool, project: Project) {
    if (!this.isAppOnline(true)) {
      return;
    }

    const title = this.translateService.instant('MM.pool.leftPane.deleteDialog.title');
    const content = this.translateService.instant('MM.pool.leftPane.deleteDialog.content', { poolName: pool.Name });

    const confirmResult = await this.dialogService.showDeleteConfirmationDialog({
      title,
      content
    });

    if (confirmResult?.value) {
      try {
        await this.poolEditDataService.deletePool(project, pool);
        this.notificationService.success(this.translateService.instant('MM.pool.leftPane.form.successDeletingPool'));
      } catch (error) {
        this.logger.error(error, '', 'PoolEditViewService');
        this.notificationService.error(this.translateService.instant('MM.pool.leftPane.form.errorDeletingPool'));
      }
    }
  }

  isAppOnline(showNotification: boolean) {
    if (showNotification && !this.isOnline) {
      this.dialogService.showOfflineNotificationDialog();
    }
    return this.isOnline;
  }

  ngOnDestroy() {
    this.subcriptions.forEach(subscription => subscription.unsubscribe());
  }

  getPoolFormConfig(dialogData: IPoolEditDialogData) {
    const formConfig = this.poolFormService.getPoolFormConfig(dialogData);
    return formConfig;
  }

  getPoolVisibilityEnumFromText(poolVisibilityText: PoolVisibilityTextEnum) {
    switch (poolVisibilityText) {
      case PoolVisibilityTextEnum.Customer:
        return PoolVisibilityEnum.Customer;
      case PoolVisibilityTextEnum.Contractor:
        return PoolVisibilityEnum.Contractor;
      case PoolVisibilityTextEnum.SubContractor:
        return PoolVisibilityEnum.SubContractor;
      default:
        return PoolVisibilityEnum.None;
    }
  }

  private async checkPoolsAvailableOrShowDialog(): Promise<boolean> {
    const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
    if (!currentProject) {
      return false;
    }

    const pools = await firstValueFrom(this.downloadService.downloadPools(currentProject));
    
    if (pools.length === 0) {
      await this.dialogService.showPromptDialog({
        title: this.translateService.instant('MM.pool.noPoolsAvailable.title'),
        content: this.translateService.instant('MM.pool.noPoolsAvailable.content'),
        buttons: [{
          text: this.translateService.instant('MM.general.button.ok'),
          type: MydocmaPromptButtonTypeEnum.primary,
          value: true
        }]
      });
      return false;
    }

    return true;
  }

  async openPoolFaultToggleEditView(fault: Fault, isAdd: boolean) {
    if (!(await this.checkPoolsAvailableOrShowDialog())) {
      return;
    }

    const dialogData: IPoolListToggleDialogData = {
      fault,
      isAdd
    };

    this.dialogRef = this.dialog.open(PoolListToggleComponent, {
      data: dialogData,
      panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-layout-edit-dialog', 'mydocma-mm-pool-list-toggle-dialog'],
      autoFocus: false,
      disableClose: true
    });

    this.dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const faultsInternal = (result.fault ? [result.fault] : result.faults) || [];
        const { allPoolsSuccess, allPoolsFailed, successfulPoolIds } = await this.poolFaultToggle(faultsInternal, result.selectedPoolIds, isAdd);

        if (successfulPoolIds.length > 0) {
          successfulPoolIds.forEach(poolId => this.recentlyUsedPoolsService.addRecentlyUsedPool(poolId));
          try {
            const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
            const updatedPools = await this.poolEditDataService.getPoolsWithUpdatedCounts(currentProject.Id, successfulPoolIds);
            const poolCountChanges = updatedPools.map(pool => ({
              poolId: pool.Id,
              newCount: pool.Count
            }));
            this.poolCountChangedSubject.next(poolCountChanges);
          } catch (error) {
            this.logger.error('Failed to get updated pool counts, faultId: ' + fault?.id + ', poolIds: ' + successfulPoolIds?.join(',') + ', isAdd: ' + isAdd, '', 'PoolEditViewService');
          }
        }

        this.checkAndTriggerFaultRefresh(successfulPoolIds, [fault.id], isAdd);

        if (allPoolsSuccess) {
          const key = isAdd ? 'MM.pool.listToggle.successAddingFaultPools' : 'MM.pool.listToggle.successDeletingFaultPools';
          this.notificationService.success(this.translateService.instant(key));
        } else if (allPoolsFailed) {
          const key = isAdd ? 'MM.pool.listToggle.errorAddingAllFaultPools' : 'MM.pool.listToggle.errorDeletingAllFaultPools';
          this.logger.error(this.translateService.instant(key), '', 'PoolEditViewService');
          this.notificationService.error(this.translateService.instant(key));
        } else {
          const key = isAdd ? 'MM.pool.listToggle.errorAddingSomeFaultPools' : 'MM.pool.listToggle.errorDeletingSomeFaultPools';
          this.logger.error(this.translateService.instant(key), '', 'PoolEditViewService');
          this.notificationService.error(this.translateService.instant(key));
        }
        return result;
      }
    });
  }

  async poolFaultToggle(faults: Fault[], selectedPoolIds: number[], isAdd: boolean) {
    const result = {
      allPoolsSuccess: false,
      allPoolsFailed: false,
      dataLoaded: false,
      successfulPoolIds: [] as number[]
    };
    if (!this.isAppOnline(true)) {
      return result;
    }

    const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
    if (!currentProject || !currentProject.Id) {
      this.logger.error('No current project found', '', 'PoolEditViewService');
      return result;
    }

    const defects = faults.map(f => ({Id: f.id, SUid: f.data.SUid, UniqueId: f.uid} as IPoolDefect));

    const poolsWithErrors: number[] = [];
    const poolsWithSuccess: number[] = [];
    const poolsSkipped: number[] = [];

    try {
      for (const poolId of selectedPoolIds) {
        if (isAdd) {
          try {
            await this.poolEditDataService.addDefectsToPool(currentProject.Id, poolId, defects);
            poolsWithSuccess.push(poolId);
            if(this.platformModeStateService.isLocalModeEnabled){
              await this.savePoolIdsToFault(faults, poolId, currentProject.MyDocmaUid, "add");
            }
          } catch (error) {
            this.logger.error(error, '', 'PoolEditViewService');
            poolsWithErrors.push(poolId);
          }
        } else {
          try {
            await this.poolEditDataService.removeDefectsFromPool(currentProject.Id, poolId, defects);
            poolsWithSuccess.push(poolId);

            if(this.platformModeStateService.isLocalModeEnabled){
              await this.savePoolIdsToFault(faults, poolId, currentProject.MyDocmaUid, "remove");
            }
          } catch (error) {
            this.logger.error(error, '', 'PoolEditViewService');
            poolsWithErrors.push(poolId);
          }
        }
      }



      const faultIds = faults.map(f => f.id);
      if (isAdd) {
        this.poolFaultTracker.trackAdded(faultIds, poolsWithSuccess);
      } else {
        this.poolFaultTracker.trackRemoved(faultIds, poolsWithSuccess);
      }

      const effectivePoolCount = selectedPoolIds.length - poolsSkipped.length;
      result.allPoolsSuccess = effectivePoolCount === 0 || poolsWithSuccess.length === effectivePoolCount;
      result.allPoolsFailed = effectivePoolCount > 0 && poolsWithErrors.length === effectivePoolCount;
      result.dataLoaded = true;
      result.successfulPoolIds = poolsWithSuccess;
      return result;

    } catch (error) {
      this.logger.error(error, '', 'PoolEditViewService');
      result.dataLoaded = true;
      result.allPoolsFailed = true;
      result.allPoolsSuccess = false;
      return result;
    }
  }

  async openPoolMultipleFaultToggleEditView(faults: Fault[], isAdd: boolean) {
    if (!(await this.checkPoolsAvailableOrShowDialog())) {
      return;
    }

    const multipleFaults = faults.length > 1;
    const dialogData: IPoolListToggleDialogData = {
      faults,
      isAdd
    };
    this.dialogRef = this.dialog.open(PoolListToggleComponent, {
      data: dialogData,
      panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-pool-list-toggle-dialog'],
      autoFocus: false,
      disableClose: true
    });

    this.dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const { selectedPoolIds, isAdd, faults } = result;
        let successCount = 0;
        let failedCount = 0;
        const allPoolCountChanges: {poolId: number, countChange?: number, newCount?: number}[] = [];

        const allSuccessfulPoolIds = new Set<number>();

        const pagedFaults =  pageBySize<Fault>(faults, 5000);
        for (const f of pagedFaults) {
          const { allPoolsSuccess: faultAllPoolsSuccess, allPoolsFailed: faultAllPoolsFailed, successfulPoolIds } = await this.poolFaultToggle(f, selectedPoolIds, isAdd);
          if (faultAllPoolsSuccess) {
            successCount += f.length;
          } else if (faultAllPoolsFailed) {
            failedCount += f.length;
          }

          successfulPoolIds.forEach(poolId => allSuccessfulPoolIds.add(poolId));
        }



        if (allSuccessfulPoolIds.size > 0) {
          allSuccessfulPoolIds.forEach(poolId => this.recentlyUsedPoolsService.addRecentlyUsedPool(poolId));
          try {
            const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
            const updatedPools = await this.poolEditDataService.getPoolsWithUpdatedCounts(currentProject.Id, Array.from(allSuccessfulPoolIds));
            allPoolCountChanges.push(...updatedPools.map(pool => ({
              poolId: pool.Id,
              newCount: pool.Count
            })));
          } catch (error) {
            this.logger.error('Failed to get updated pool counts, poolIds: ' + Array.from(allSuccessfulPoolIds).join(',') + ', isAdd: ' + isAdd + ', faultCount: ' + faults?.length, '', 'PoolEditViewService');
          }
        }

        if (allPoolCountChanges.length > 0) {

          this.poolCountChangedSubject.next(allPoolCountChanges);
        }

        this.checkAndTriggerFaultRefresh(Array.from(allSuccessfulPoolIds), faults.map(f => f.id), result.isAdd);

        if (successCount === faults.length) {
          const key = isAdd ? 'MM.pool.listToggle.successAddingFaultsPools' : 'MM.pool.listToggle.successDeletingFaultsPools';
          this.notificationService.success(this.translateService.instant(key));
        } else if (failedCount === faults.length) {
          const key = isAdd ? 'MM.pool.listToggle.errorAddingFaultsPools' : 'MM.pool.listToggle.errorDeletingFaultsPools';
          this.logger.error(this.translateService.instant(key), '', 'PoolEditViewService');
          this.notificationService.error(this.translateService.instant(key));
        } else {
          const key = isAdd ? 'MM.pool.listToggle.errorAddingSomeFaultsPools' : 'MM.pool.listToggle.errorDeletingSomeFaultsPools';
          this.logger.error(this.translateService.instant(key), '', 'PoolEditViewService');
          this.notificationService.error(this.translateService.instant(key));
        }
      }
      return result;
    });
  }

  private async checkAndTriggerFaultRefresh(successfulPoolIds: number[], faultIds: number[], isAdd: boolean) {
    try {
      const filterState = await firstValueFrom(this.filterService.state);
      const affectedPools: number[] = [];

      if (filterState?.poolInclusionFilters?.length > 0) {
        const inclusionPoolIds = filterState.poolInclusionFilters.map(p => p.Id);
        affectedPools.push(...successfulPoolIds.filter(id => inclusionPoolIds.includes(id)));
      }

      if (filterState?.poolExclusionFilters?.length > 0) {
        const exclusionPoolIds = filterState.poolExclusionFilters.map(p => p.Id);
        affectedPools.push(...successfulPoolIds.filter(id => exclusionPoolIds.includes(id)));
      }

      if (affectedPools.length > 0) {
        this.poolMembershipChangedSubject.next({
          faultIds,
          poolIds: affectedPools,
          operation: isAdd ? 'ADD' : 'REMOVE'
        });
      }
    } catch (error) {
    }
  }

  private async savePoolIdsToFault(faults: Fault[], poolId: number, mydocmaUid: string, action: "add" | "remove") {
    for(const fault of faults){
      try {
        if(action === "add"){
          const poolIds = fault?.loadState?.data?.PoolIds ?? [];
          if (fault?.loadState?.data) {
            fault.loadState.data.PoolIds = poolIds.includes(poolId)? poolIds : [...poolIds, poolId];
            fault.data.PoolIds = poolIds.includes(poolId)? poolIds : [...poolIds, poolId];
          }
        } else if(action === "remove"){
          fault.loadState.data.PoolIds = fault?.loadState?.data?.PoolIds.filter(id => id !== poolId) || [];
          fault.data.PoolIds = fault.data.PoolIds.filter(id => id !== poolId) || [];
          
        }
        await this.faultPersistensService.saveLoadState(mydocmaUid, fault.uid, fault.loadState);
      } catch (error) {
        this.logger.error(error,'','PoolEditViewService');
      }
    }
  }
}
